import { FC, useState, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { routingUsageMessage } from "../../helpers/routingUsage";
import { IRouteItem } from "../../types/order/IRouteItem";
import { Timestamp } from "../../types";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserSession } from "../../context/UserContext";
import {
  getDriverRoutes,
  getDriverPositionByEmail,
  getRouteOptimizationCapability,
  previewOptimizedDriverRoute,
} from "../../api/client";
import moment from "moment";
import Driver from "../../models/Driver";
import {
  getRouteValidationError,
  RouteValidationError,
} from "./routeValidator";
import {
  PlannerRouteStop,
  mapDriverRoutesResponse,
  pickupBeforeDeliverySort,
  updateStopNumbers,
} from "./mapDriverRoutes";
import {
  getRouteItemId,
  getPhysicalVisitKey,
  groupCompatibleRouteStops,
} from "../../helpers/routeMappers";
import {
  RouteDroppableZone,
  StopCardWrapper,
  FloatingRouteActions,
} from "@components";
import { Dropdown } from "flowbite-react";
import {
  HiChevronDown,
  HiOutlineExclamation,
  HiOutlineClipboardList,
  HiLightningBolt,
} from "react-icons/hi";

import { Map } from "../orders/Map/components/Map";
import { panelSm } from "@design-system";
import { MapRoute } from "../orders/Map/components/MapRoute";
import { IMapRoute } from "../orders/Map/types";
import { libraries } from "../orders/Map/types";
import { OrderStatus } from "../../types/order/IOrder";
import "../orders/Map/components/map-styles.css";
import {
  DndContext,
  DragEndEvent,
  useSensors,
  useSensor,
  PointerSensor,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  pointerWithin,
  closestCorners,
  CollisionDetection,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

/**
 * MapRoutePlanner Component - Driver Position Integration
 *
 * This component integrates driver position tracking for the route planner:
 * - Fetches real-time driver position when a driver is selected
 * - Displays truck marker showing current driver location on the map
 * - Helps planners see where the driver is relative to planned route
 */

// Custom collision detection that combines algorithms for better precision
const customCollisionDetection: CollisionDetection = (args) => {
  // First try closest corners for better detection between items
  const cornerCollisions = closestCorners(args);

  if (cornerCollisions.length > 0) {
    return cornerCollisions;
  }

  // Fall back to pointer within for more precise detection of drop areas
  return pointerWithin(args);
};

interface MapRoutePlannerProps {
  selectedDriver: string;
  selectedDate: Date;
  onChange: (state: {
    hasChanges: boolean;
    changesCount: number;
    routeStops: (IRouteItem & { isAvailable: boolean })[];
    availableStops: (IRouteItem & { isAvailable: boolean })[];
    hasValidationErrors: boolean;
  }) => void;
  reset?: boolean;
  onSelectDriver: (driverId: string) => void;
  drivers: Driver[];
  onSaveChanges: () => void;
  onDiscardChanges: () => void;
  isSaving: boolean;
  hasRouteChanges: boolean;
  hasRouteValidationErrors: boolean;
}

export const MapRoutePlanner: FC<MapRoutePlannerProps> = ({
  selectedDriver,
  selectedDate,
  onChange,
  reset,
  onSelectDriver,
  drivers,
  onSaveChanges,
  onDiscardChanges,
  isSaving,
  hasRouteChanges,
  hasRouteValidationErrors,
}) => {
  const { t, i18n } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const [loading, setLoading] = useState(false);
  const [routeUpdating, setRouteUpdating] = useState(false);
  const [availableStops, setAvailableStops] = useState<PlannerRouteStop[]>([]);
  const [routeStops, setRouteStops] = useState<PlannerRouteStop[]>([]);
  const [originalRouteStops, setOriginalRouteStops] = useState<
    PlannerRouteStop[]
  >([]);
  const [originalAvailableStops, setOriginalAvailableStops] = useState<
    PlannerRouteStop[]
  >([]);
  const [validationError, setValidationError] =
    useState<RouteValidationError | null>(null);
  const [userHasMadeChanges, setUserHasMadeChanges] = useState(false);
  const [selectedMapRoute, setSelectedMapRoute] = useState<
    IMapRoute | undefined
  >();
  const [driverPosition, setDriverPosition] =
    useState<google.maps.LatLngLiteral>();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isUnassignedCollapsed, setIsUnassignedCollapsed] = useState(false);
  const [optimizationEnabled, setOptimizationEnabled] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedFingerprint, setOptimizedFingerprint] = useState<
    string | null
  >(null);
  const [optimizationError, setOptimizationError] = useState<string | null>(
    null,
  );

  const routeFingerprint = (stops: PlannerRouteStop[]) =>
    stops.map(getRouteItemId).join("|");
  const physicalVisitCount = new Set(routeStops.map(getPhysicalVisitKey)).size;
  const routeIsOptimized =
    optimizedFingerprint !== null &&
    optimizedFingerprint === routeFingerprint(routeStops);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const { isLoaded, loadError } = useLoadScript({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  useEffect(() => {
    const loadCapability = async () => {
      if (!userSession?.token || !activeWorkspace?.workspaceId) {
        setOptimizationEnabled(false);
        return;
      }
      try {
        const capability = await getRouteOptimizationCapability(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
        );
        setOptimizationEnabled(capability?.enabled === true);
      } catch (error) {
        console.error("Error fetching route optimization capability:", error);
        setOptimizationEnabled(false);
      }
    };
    loadCapability();
  }, [userSession?.token, activeWorkspace?.workspaceId]);

  // Clear stops when driver changes
  useEffect(() => {
    setAvailableStops([]);
    setRouteStops([]);
    setOriginalRouteStops([]);
    setUserHasMadeChanges(false);
    setDriverPosition(undefined);
    setOptimizedFingerprint(null);
    setOptimizationError(null);
  }, [selectedDriver]);

  // Fetch driver position when driver is selected
  useEffect(() => {
    const fetchDriverPosition = async () => {
      if (
        selectedDriver &&
        userSession?.token &&
        activeWorkspace?.workspaceId
      ) {
        try {
          const positionData = await getDriverPositionByEmail(
            userSession.token.accessToken,
            activeWorkspace.workspaceId,
            selectedDriver,
          );

          if (positionData?.lastLocation) {
            const position = {
              lat: positionData.lastLocation.lat,
              lng: positionData.lastLocation.lon,
            };
            console.log("Route Planner - Setting driver position:", position);
            setDriverPosition(position);
          } else {
            console.log("Route Planner - No position data found");
            setDriverPosition(undefined);
          }
        } catch (error) {
          console.error("Error fetching driver position:", error);
          setDriverPosition(undefined);
        }
      } else {
        setDriverPosition(undefined);
      }
    };

    fetchDriverPosition();
  }, [selectedDriver, userSession?.token, activeWorkspace?.workspaceId]);

  // Reset state when reset prop changes
  useEffect(() => {
    if (reset) {
      setRouteStops(originalRouteStops);
      setAvailableStops(originalAvailableStops);
      setUserHasMadeChanges(false);
      setValidationError(null);
      setOptimizedFingerprint(null);
      setOptimizationError(null);

      // Re-fetch data to ensure we have the latest state from the server
      const fetchRoutes = async () => {
        if (
          !selectedDriver ||
          !userSession?.token ||
          !activeWorkspace?.workspaceId
        )
          return;

        setLoading(true);
        try {
          const formattedDate = moment(selectedDate).format("YYYY-MM-DD");

          const response = await getDriverRoutes(
            userSession.token.accessToken,
            activeWorkspace.workspaceId,
            selectedDriver,
            formattedDate,
          );

          if (response) {
            const { newStops, scheduledStops } =
              mapDriverRoutesResponse(response);

            setAvailableStops(newStops);
            setRouteStops(scheduledStops);
            setOriginalRouteStops(scheduledStops);
            setOriginalAvailableStops(newStops);
            setUserHasMadeChanges(false);
            setOptimizedFingerprint(null);
            setOptimizationError(null);
          }
        } catch (error) {
          console.error("Error fetching driver routes:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchRoutes();
    }
  }, [
    reset,
    selectedDriver,
    selectedDate,
    userSession?.token,
    activeWorkspace?.workspaceId,
  ]);

  // Fetch routes when driver or date changes
  useEffect(() => {
    const fetchRoutes = async () => {
      if (
        !selectedDriver ||
        !userSession?.token ||
        !activeWorkspace?.workspaceId
      )
        return;

      setLoading(true);
      try {
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");

        const response = await getDriverRoutes(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          selectedDriver,
          formattedDate,
        );

        if (response) {
          const { newStops, scheduledStops } =
            mapDriverRoutesResponse(response);

          setAvailableStops(newStops);
          setRouteStops(scheduledStops);
          setOriginalRouteStops(scheduledStops);
          setOriginalAvailableStops(newStops);
          setUserHasMadeChanges(false);
          setOptimizedFingerprint(null);
          setOptimizationError(null);
        }
      } catch (error) {
        console.error("Error fetching driver routes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [
    selectedDriver,
    selectedDate,
    userSession?.token,
    activeWorkspace?.workspaceId,
  ]);

  // Update onChange when state changes
  useEffect(() => {
    const changesCount = userHasMadeChanges ? 1 : 0;
    onChange({
      hasChanges: userHasMadeChanges,
      changesCount,
      routeStops: routeStops,
      availableStops: availableStops,
      hasValidationErrors: validationError !== null,
    });
  }, [
    userHasMadeChanges,
    validationError,
    onChange,
    routeStops,
    availableStops,
  ]);

  // Update map route when route stops change
  useEffect(() => {
    if (routeStops.length > 0) {
      setRouteUpdating(true);

      // Small delay to ensure the map update is visible to the user
      const updateTimeout = setTimeout(() => {
        setSelectedMapRoute({
          id: selectedDriver,
          route: routeStops,
          customerName:
            drivers.find((d) => d.email === selectedDriver)?.firstName || "",
          customer: {
            id: "",
            name:
              drivers.find((d) => d.email === selectedDriver)?.firstName || "",
            firstName:
              drivers.find((d) => d.email === selectedDriver)?.firstName || "",
            lastName:
              drivers.find((d) => d.email === selectedDriver)?.lastName || "",
            contactEmail: selectedDriver,
            phoneNumber: "",
            isCompany: false,
            workspaceId: activeWorkspace?.workspaceId || "",
            createdAt: Timestamp.now(),
          },
          orderNumber: "",
          type: "Pickup",
          status: OrderStatus.Scheduled,
          driverId: selectedDriver,
          driver: drivers.find((d) => d.email === selectedDriver),
        });

        // Add a small delay before removing the loading state
        setTimeout(() => {
          setRouteUpdating(false);
        }, 300);
      }, 100);

      return () => clearTimeout(updateTimeout);
    } else {
      setSelectedMapRoute(undefined);
      setRouteUpdating(false);
    }
  }, [routeStops, selectedDriver, drivers, activeWorkspace?.workspaceId]);

  const handleMoveToRoute = (stopId: string) => {
    const stopToMove = availableStops.find(
      (stop) => getRouteItemId(stop) === stopId,
    );
    if (stopToMove) {
      setRouteUpdating(true);
      const newStops = [...routeStops, { ...stopToMove, isAvailable: false }];
      const updatedStops = updateStopNumbers(newStops);

      const remainingAvailableStops = availableStops.filter(
        (stop) => getRouteItemId(stop) !== stopId,
      );
      const error = getRouteValidationError(
        updatedStops,
        remainingAvailableStops,
      );
      setValidationError(error);

      setAvailableStops((prev) =>
        prev.filter((stop) => getRouteItemId(stop) !== stopId),
      );
      setRouteStops(updatedStops);
      setUserHasMadeChanges(true);
    }
  };

  const handleMoveToAvailable = (stopId: string) => {
    const stopToMove = routeStops.find(
      (stop) => getRouteItemId(stop) === stopId,
    );
    if (stopToMove) {
      setRouteUpdating(true);
      const newRouteStops = routeStops.filter(
        (stop) => getRouteItemId(stop) !== stopId,
      );
      const updatedStops = updateStopNumbers(newRouteStops);

      const newAvailableStops = [
        ...availableStops,
        { ...stopToMove, stopNumber: 0, isAvailable: true },
      ];
      const error = getRouteValidationError(updatedStops, newAvailableStops);
      setValidationError(error);

      setRouteStops(updatedStops);
      setAvailableStops((prev) => [
        ...prev,
        { ...stopToMove, stopNumber: 0, isAvailable: true },
      ]);
      setUserHasMadeChanges(true);
    }
  };

  const handleMoveOrderToRoute = (orderId: string) => {
    const groupStops = availableStops.filter(
      (stop) => stop.orderId === orderId,
    );
    if (groupStops.length > 0) {
      setRouteUpdating(true);
      // Sort stops so pickup comes before delivery for the same order
      const sortedStops = pickupBeforeDeliverySort(groupStops);

      // Move all stops in the order to the route at once
      const stopsToMove = sortedStops.map((stop) => ({
        ...stop,
        isAvailable: false,
      }));
      const newRouteStops = [...routeStops, ...stopsToMove];
      const updatedStops = updateStopNumbers(newRouteStops);

      const remainingAvailableStops = availableStops.filter(
        (stop) => stop.orderId !== orderId,
      );
      const error = getRouteValidationError(
        updatedStops,
        remainingAvailableStops,
      );
      setValidationError(error);

      setRouteStops(updatedStops);
      setAvailableStops(remainingAvailableStops);
      setUserHasMadeChanges(true);
    }
  };

  const handleOptimizeRoute = async () => {
    if (
      !optimizationEnabled ||
      isOptimizing ||
      physicalVisitCount < 2 ||
      validationError ||
      !selectedDriver ||
      !userSession?.token ||
      !activeWorkspace?.workspaceId
    ) {
      return;
    }

    setIsOptimizing(true);
    setOptimizationError(null);
    try {
      const routeDate = moment(selectedDate).format("YYYY-MM-DD");
      const optimization = await previewOptimizedDriverRoute(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        selectedDriver,
        routeDate,
      );
      const proposedIds = optimization?.proposedOrderedRouteIds;
      if (!Array.isArray(proposedIds)) {
        throw new Error("Optimization response did not contain a route order");
      }

      const stopsById = new globalThis.Map(
        routeStops.map((stop) => [getRouteItemId(stop), stop]),
      );
      if (
        proposedIds.length !== routeStops.length ||
        proposedIds.some((routeId: string) => !stopsById.has(routeId))
      ) {
        throw new Error(
          "Optimization response did not contain the complete route",
        );
      }

      const optimizedStops = updateStopNumbers(
        groupCompatibleRouteStops(
          proposedIds.map((routeId: string) => stopsById.get(routeId)!),
        ),
      );
      const changed =
        routeFingerprint(optimizedStops) !== routeFingerprint(routeStops);
      setRouteStops(optimizedStops);
      setValidationError(
        getRouteValidationError(optimizedStops, availableStops),
      );
      setOptimizedFingerprint(routeFingerprint(optimizedStops));
      if (changed) setUserHasMadeChanges(true);
    } catch (error) {
      console.error("Error optimizing route:", error);
      const serverMessage = (
        error as { response?: { data?: { error?: unknown } } }
      )?.response?.data?.error;
      setOptimizationError(
        routingUsageMessage(error, t, i18n.language) ??
          (typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : t(
                "planner:route_planner.optimization_failed",
                "Route optimization failed. Check the stops and try again.",
              )),
      );
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current) {
      setActiveId(active.data.current.id as string);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    // Only handle container changes during dragOver
    // This prevents items from jumping between containers during drag
    const activeIsAvailable = active.data.current?.isAvailable;
    const overId = over.id;

    // We don't want to do anything if dragging over the same container
    if (
      (activeIsAvailable && overId === "available-zone") ||
      (!activeIsAvailable && overId === "route-zone")
    ) {
      return;
    }

    // We also don't want to do anything if dragging over another item
    if (overId !== "available-zone" && overId !== "route-zone") {
      return;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    // Handle order group dragging
    if (active.id.toString().startsWith("group-")) {
      const orderId = active.id.toString().replace("group-", "");
      const groupStops = availableStops.filter(
        (stop) => stop.orderId === orderId,
      );

      if (over.id === "route-zone" && groupStops.length > 0) {
        // Move all stops in the group to the route at once
        setRouteUpdating(true);
        // Sort stops so pickup comes before delivery for the same order
        const sortedStops = pickupBeforeDeliverySort(groupStops);
        const stopsToMove = sortedStops.map((stop) => ({
          ...stop,
          isAvailable: false,
        }));
        const newRouteStops = [...routeStops, ...stopsToMove];
        const updatedStops = updateStopNumbers(newRouteStops);

        const remainingAvailableStops = availableStops.filter(
          (stop) => stop.orderId !== orderId,
        );
        const error = getRouteValidationError(
          updatedStops,
          remainingAvailableStops,
        );
        setValidationError(error);

        setRouteStops(updatedStops);
        setAvailableStops(remainingAvailableStops);
        setUserHasMadeChanges(true);
      } else if (
        over.id.toString() !== "available-zone" &&
        !over.id.toString().startsWith("group-")
      ) {
        // Dropping a group onto a specific stop in the route
        setRouteUpdating(true);
        const targetStopId = over.id.toString();
        const targetIndex = routeStops.findIndex(
          (stop) => getRouteItemId(stop) === targetStopId,
        );

        if (targetIndex !== -1) {
          // Move all stops in the group to the route at the target position
          // Sort stops so pickup comes before delivery for the same order
          const sortedStops = pickupBeforeDeliverySort(groupStops);
          const stopsToMove = sortedStops.map((stop) => ({
            ...stop,
            isAvailable: false,
          }));
          const newRouteStops = [
            ...routeStops.slice(0, targetIndex + 1),
            ...stopsToMove,
            ...routeStops.slice(targetIndex + 1),
          ];
          const updatedStops = updateStopNumbers(newRouteStops);

          const remainingAvailableStops = availableStops.filter(
            (stop) => stop.orderId !== orderId,
          );
          const error = getRouteValidationError(
            updatedStops,
            remainingAvailableStops,
          );
          setValidationError(error);

          setRouteStops(updatedStops);
          setAvailableStops(remainingAvailableStops);
          setUserHasMadeChanges(true);
        }
      }

      setActiveId(null);
      return;
    }

    // Handle drops between different containers
    if (
      over.id === "available-zone" &&
      active.data.current?.isAvailable === false
    ) {
      // Moving from route to available
      handleMoveToAvailable(active.id.toString());
    } else if (
      over.id === "route-zone" &&
      active.data.current?.isAvailable === true
    ) {
      // Moving from available to route
      handleMoveToRoute(active.id.toString());
    } else if (active.id !== over.id) {
      // Handle reordering within the same container
      const isInRouteContainer = active.data.current?.isAvailable === false;

      if (isInRouteContainer) {
        // Reordering within route container
        setRouteUpdating(true);
        const currentIndex = routeStops.findIndex(
          (stop) => getRouteItemId(stop) === active.id,
        );
        const newIndex = routeStops.findIndex(
          (stop) => getRouteItemId(stop) === over.id,
        );

        if (currentIndex !== -1 && newIndex !== -1) {
          const newRouteStops = arrayMove(routeStops, currentIndex, newIndex);
          const updatedStops = updateStopNumbers(newRouteStops);

          // Add validation when reordering within route
          const error = getRouteValidationError(updatedStops, availableStops);
          setValidationError(error);

          setRouteStops(updatedStops);
          setUserHasMadeChanges(true);
        }
      } else {
        // Reordering within available container
        const currentIndex = availableStops.findIndex(
          (stop) => getRouteItemId(stop) === active.id,
        );
        const newIndex = availableStops.findIndex(
          (stop) => getRouteItemId(stop) === over.id,
        );

        if (currentIndex !== -1 && newIndex !== -1) {
          const newAvailableStops = arrayMove(
            availableStops,
            currentIndex,
            newIndex,
          );
          setAvailableStops(newAvailableStops);
          setUserHasMadeChanges(true);
        }
      }
    } else if (
      over.id.toString() !== "available-zone" &&
      over.id.toString() !== "route-zone" &&
      active.data.current?.isAvailable === true
    ) {
      // Dropping an available stop onto a specific stop in the route
      setRouteUpdating(true);
      const targetStopId = over.id.toString();
      const targetIndex = routeStops.findIndex(
        (stop) => getRouteItemId(stop) === targetStopId,
      );
      const stopToMove = availableStops.find(
        (stop) => getRouteItemId(stop) === active.id,
      );

      if (targetIndex !== -1 && stopToMove) {
        // Insert the stop at the specific position in the route
        const newRouteStops = [
          ...routeStops.slice(0, targetIndex + 1),
          { ...stopToMove, isAvailable: false },
          ...routeStops.slice(targetIndex + 1),
        ];
        const updatedStops = updateStopNumbers(newRouteStops);

        const remainingAvailableStops = availableStops.filter(
          (stop) => getRouteItemId(stop) !== active.id,
        );
        const error = getRouteValidationError(
          updatedStops,
          remainingAvailableStops,
        );
        setValidationError(error);

        setRouteStops(updatedStops);
        setAvailableStops(remainingAvailableStops);
        setUserHasMadeChanges(true);
      }
    }

    setActiveId(null);
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="relative h-full w-full">
        {/* Map */}
        <Map
          selectedRoute={selectedMapRoute}
          isLoading={loading || routeUpdating}
          hasListOverlay={false}
          message={
            !selectedDriver
              ? t("planner:route_planner.select_driver")
              : undefined
          }
          messageTitle={
            !selectedDriver
              ? t("planner:route_planner.no_driver_selected")
              : undefined
          }
        >
          <MapRoute
            selectedRoute={selectedMapRoute}
            driverPosition={driverPosition}
          />
        </Map>

        {/* Side Panel Overlay */}
        <div className="fixed sm:absolute top-2 bottom-2 left-2 right-2 sm:top-4 sm:bottom-4 sm:left-4 sm:right-auto sm:w-[420px] flex flex-col bg-white/95 dark:bg-gray-800/95 shadow-xl rounded-lg">
          {/* Driver dropdown */}
          <div className="p-2">
            <Dropdown
              className="w-full"
              inline
              arrowIcon={false}
              disabled={loading || routeUpdating || isSaving}
              theme={{
                inlineWrapper: "flex items-center w-full",
              }}
              label={
                <div
                  className={`w-full flex items-center justify-between bg-white rounded-lg pl-3 py-2 border text-sm 
                              ${
                                loading || routeUpdating || isSaving
                                  ? "cursor-not-allowed opacity-50 border-gray-300 pointer-events-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-500"
                                  : "cursor-pointer border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                              }
                              active:scale-95`}
                >
                  <span
                    className={`flex-1 ${selectedDriver ? (loading || routeUpdating || isSaving ? "text-muted" : "text-secondary") : loading || routeUpdating || isSaving ? "text-muted" : "text-muted"}`}
                  >
                    {selectedDriver
                      ? drivers.find((d) => d.email === selectedDriver)
                          ?.firstName +
                        " " +
                        drivers.find((d) => d.email === selectedDriver)
                          ?.lastName
                      : t(
                          "planner:route_planner.select_driver_placeholder",
                          "Select a driver",
                        )}
                  </span>
                  <HiChevronDown
                    className={`${loading || routeUpdating || isSaving ? "text-muted" : "text-muted"}`}
                  />
                </div>
              }
            >
              <Dropdown.Header>
                <span className="block text-xs text-muted px-2">
                  {t(
                    "planner:route_planner.assign_to_driver",
                    "Assign to driver",
                  )}
                </span>
              </Dropdown.Header>
              {drivers?.length > 0 ? (
                drivers.map((driver) => (
                  <Dropdown.Item
                    key={driver.email}
                    onClick={() => onSelectDriver(driver.email)}
                    className="text-sm"
                  >
                    {driver.firstName} {driver.lastName} ({driver.email})
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled className="text-sm">
                  {t(
                    "planner:route_planner.no_drivers_available",
                    "No drivers available",
                  )}
                </Dropdown.Item>
              )}
            </Dropdown>
          </div>
          {/* Single Column Layout */}
          <div className="flex-1 min-h-0 border-t border-border overflow-y-auto">
            <div className="flex flex-col h-full">
              {/* Unassigned Stops Warning/Accordion */}
              {availableStops.length > 0 && (
                <div className="border-b border-border">
                  <button
                    type="button"
                    onClick={() =>
                      setIsUnassignedCollapsed(!isUnassignedCollapsed)
                    }
                    className="w-full flex items-center justify-between p-3 text-left bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <HiOutlineExclamation className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                      <div>
                        <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          {t(
                            "planner:route_planner.unassigned_stops_warning",
                            "{{count}} stops have not been planned",
                            { count: availableStops.length },
                          )}
                        </div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-300">
                          {t(
                            "planner:route_planner.click_to_view",
                            "Click to view and add stops",
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`transform transition-transform ${isUnassignedCollapsed ? "rotate-0" : "rotate-180"}`}
                    >
                      <HiChevronDown className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                  </button>

                  {!isUnassignedCollapsed && (
                    <div className="bg-surface">
                      <RouteDroppableZone
                        id="available-zone"
                        stops={availableStops}
                        onMoveToRoute={handleMoveToRoute}
                        onMoveOrderToRoute={handleMoveOrderToRoute}
                        invalidStopIds={validationError?.invalidStopIds}
                        className="p-2"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Planned Route */}
              <div className="flex-1 min-h-0">
                <div
                  className={`sticky top-0 bg-surface border-b border-border px-3 py-2 z-10`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-secondary truncate">
                      {t(
                        "planner:route_planner.planned_route",
                        "Planned route",
                      )}{" "}
                      ({physicalVisitCount})
                    </h3>
                    {optimizationEnabled && (
                      <button
                        type="button"
                        onClick={handleOptimizeRoute}
                        disabled={
                          isOptimizing ||
                          routeIsOptimized ||
                          physicalVisitCount < 2 ||
                          validationError !== null
                        }
                        title={optimizationError || undefined}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-brand hover:text-brand-hover hover:bg-brand-muted px-1.5 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <HiLightningBolt
                          className={`h-3.5 w-3.5 ${isOptimizing ? "animate-pulse" : ""}`}
                        />
                        {isOptimizing
                          ? t(
                              "planner:route_planner.optimizing_route",
                              "Optimizing…",
                            )
                          : routeIsOptimized
                            ? t(
                                "planner:route_planner.route_optimized",
                                "Route optimized",
                              )
                            : t(
                                "planner:route_planner.optimize_route",
                                "Optimize route",
                              )}
                      </button>
                    )}
                  </div>
                  {optimizationError && (
                    <p
                      role="alert"
                      className="mt-1 text-xs leading-snug text-danger"
                    >
                      {optimizationError}
                    </p>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {routeStops.length > 0 ? (
                    <RouteDroppableZone
                      id="route-zone"
                      stops={routeStops}
                      onMoveToAvailable={handleMoveToAvailable}
                      invalidStopIds={validationError?.invalidStopIds}
                      className="p-2"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-muted text-sm">
                      <HiOutlineClipboardList className="h-8 w-8 mb-2 opacity-50" />
                      {t(
                        "planner:route_planner.no_planned_stops",
                        "No planned stops",
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay
          style={{
            position: "fixed",
            zIndex: 9999,
            pointerEvents: "none",
            cursor: "grabbing",
            width: "auto",
            height: "auto",
          }}
          dropAnimation={null}
        >
          {activeId ? (
            activeId.toString().startsWith("group-") ? (
              <div className={`${panelSm} p-4 shadow-lg`}>
                <div className="text-sm font-medium text-secondary">
                  {
                    availableStops.find(
                      (stop) =>
                        stop.orderId ===
                        activeId.toString().replace("group-", ""),
                    )?.orderDisplayName
                  }
                </div>
              </div>
            ) : (
              <StopCardWrapper
                stop={[...availableStops, ...routeStops].find(
                  (stop) => getRouteItemId(stop) === activeId,
                )!}
                isDragging={true}
                isInvalid={validationError?.invalidStopIds?.includes(
                  activeId.toString(),
                )}
                routeStops={routeStops}
              />
            )
          ) : null}
        </DragOverlay>

        {/* Floating Actions */}
        <FloatingRouteActions
          hasChanges={userHasMadeChanges}
          hasValidationErrors={validationError !== null}
          isSaving={isSaving}
          onSave={onSaveChanges}
          onDiscard={onDiscardChanges}
        />
      </div>
    </DndContext>
  );
};
